import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import HidePasswordIcon from '../components/HidePasswordIcon';

const regexPassword = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*\-+.]).{8,}$/;

const apiPrefix = process.env.REACT_APP_API_PREFIX;

/**
 * @file
 * @module Pages/ResetPassword
 * @description ResetPassword est une page qui permet à un utilisateur de réinitialiser son mot de passe.
 * - L'utilisateur doit saisir un nouveau mot de passe et le confirmer.
 * - Lorsque l'utilisateur soumet le formulaire, un e-mail de confirmation est envoyé.
 * - Cette page utilise le hook useState de React pour gérer l'état des champs de saisie et de l'affichage de la modale.
 * - Cette page utilise le hook useEffect de React pour vérifier la validité du token de réinitialisation de mot de passe.
 * - Cette page utilise la fonction fetch de JavaScript pour envoyer une demande de réinitialisation de mot de passe à l'API.
 */

const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const navigate = useNavigate();
    const { token } = useParams();

    useEffect(() => {
        const checkToken = async () => {
            try {
                const response = await fetch(apiPrefix + `reset/${token}`);
                if (response.status !== 200) {
                    navigate('/error');
                }
            } catch (error) {
                navigate('/error');
            }
        };

        checkToken();
    }, [token, navigate]);

    const hide = () => {
        setShowModal(false);
        if (isSuccess) {
            navigate('/signin');
        }
    };

    const resetPassword = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            setMessage('Les mots de passe ne correspondent pas');
            setShowModal(true);
            return;
        }

        if (!regexPassword.test(password)) {
            setMessage('Le mot de passe doit contenir au moins 8 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial.');
            setShowModal(true);
            return;
        }

        try {
            const response = await fetch(apiPrefix + `reset/${token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ password })
            });

            const data = await response.json();

            if (response.ok) {
                setIsSuccess(true);
            }

            setMessage(data.message);
            setShowModal(true);
        } catch (error) {
            setMessage('Une erreur est survenue lors de la réinitialisation du mot de passe.');
            setShowModal(true);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center bg-gray-50 p-5 min-h-screen">
            <h2 className="text-gray-800 text-2xl font-bold mb-5">Réinitialiser le mot de passe</h2>
            <form onSubmit={resetPassword} className="card w-full max-w-md">
                <div className="form-group">
                    <label className="form-label">
                        <i className="fas fa-key mr-2.5 text-primary"></i>
                        Nouveau mot de passe
                    </label>
                    <div className="relative">
                        <input 
                            type={showPassword ? 'text' : 'password'} 
                            placeholder="Nouveau mot de passe" 
                            value={password} 
                            onChange={e => setPassword(e.target.value)} 
                            required 
                            className="form-input h-10"
                        />
                        <HidePasswordIcon showPassword={showPassword} setShowPassword={setShowPassword} />
                    </div>
                </div>
                
                <div className="form-group">
                    <label className="form-label">
                        <i className="fas fa-key mr-2.5 text-primary"></i>
                        Confirmer le nouveau mot de passe
                    </label>
                    <input 
                        type={showPassword ? 'text' : 'password'} 
                        placeholder="Confirmer le nouveau mot de passe" 
                        value={confirmPassword} 
                        onChange={e => setConfirmPassword(e.target.value)} 
                        required 
                        className="form-input h-10"
                    />
                </div>
                
                <button type="submit" className="btn-primary w-full mt-5">Réinitialiser le mot de passe</button>
            </form>
            {showModal && (
                <div className="modal-overlay" onClick={hide}>
                    <div className="modal-content">
                        <button 
                            className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                            onClick={hide}
                        >
                            ×
                        </button>
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-primary mb-4">Information</h2>
                            <p className="text-gray-700 mb-6">{message}</p>
                            <button 
                                onClick={hide}
                                className="btn-primary"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResetPassword;
